import React, { useState } from "react";
import {
  Beaker,
  Gamepad2,
  ArrowLeft,
  Target,
  HelpCircle,
  MousePointerClick,
  Zap,
  GitCommit,
  Hash,
  Menu,
  X,
} from "lucide-react";

// Import your games (Make sure these files exist!)
import BlackJack from "./BlackJack";
import TicTacToe from "./TicTacToe";
import RockPaperScissors from "./RockPaperScissors";
import NumberGuessing from "./NumberGuessing";
import Quiz from "./Quiz";
import WhackAMole from "./WhackAMole";
import SimonSays from "./SimonSays";
import Snake from "./Snake";

const Playground = () => {
  const [activeApp, setActiveApp] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // State for burger menu

  // Define all available modules here
  const modules = [
    {
      id: "blackjack",
      name: "Blackjack",
      desc: "Card game with memory caching.",
      icon: Gamepad2,
      color: "text-green-600",
      bg: "bg-green-100",
      component: BlackJack,
    },
    {
      id: "tictactoe",
      name: "Tic Tac Toe",
      desc: "Classic 3x3 grid game.",
      icon: Hash,
      color: "text-blue-600",
      bg: "bg-blue-100",
      component: TicTacToe,
    },
    {
      id: "rps",
      name: "Rock Paper Scissors",
      desc: "Vs Computer logic test.",
      icon: Target,
      color: "text-orange-600",
      bg: "bg-orange-100",
      component: RockPaperScissors,
    },
    {
      id: "guess",
      name: "Number Guessing",
      desc: "Higher/Lower logic with state.",
      icon: HelpCircle,
      color: "text-purple-600",
      bg: "bg-purple-100",
      component: NumberGuessing,
    },
    {
      id: "quiz",
      name: "Quiz App",
      desc: "Simple array mapping & scoring.",
      icon: HelpCircle,
      color: "text-yellow-600",
      bg: "bg-yellow-100",
      component: Quiz,
    },
    {
      id: "whack",
      name: "Whack-a-Mole",
      desc: "Testing intervals and timers.",
      icon: MousePointerClick,
      color: "text-red-600",
      bg: "bg-red-100",
      component: WhackAMole,
    },
    {
      id: "simon",
      name: "Simon Says",
      desc: "Async sequence memory test.",
      icon: Zap,
      color: "text-cyan-600",
      bg: "bg-cyan-100",
      component: SimonSays,
    },
    {
      id: "snake",
      name: "Snake Game",
      desc: "Grid coordinates & keyboard events.",
      icon: GitCommit,
      color: "text-emerald-600",
      bg: "bg-emerald-100",
      component: Snake,
    },
  ];

  if (activeApp) {
    const ActiveComponent = modules.find((m) => m.id === activeApp)?.component;
    return (
      <div className="relative min-h-screen bg-slate-900">
        <button
          onClick={() => setActiveApp(null)}
          className="absolute top-4 left-4 z-50 flex items-center gap-2 bg-slate-800/80 hover:bg-slate-700 text-white px-4 py-2 rounded-xl backdrop-blur-md transition shadow-lg border border-white/10 font-semibold"
        >
          <ArrowLeft size={20} /> Exit Test
        </button>
        {ActiveComponent && <ActiveComponent />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10 font-sans">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header Area */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
                <Beaker size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  Developer Playground
                </h1>
                <p className="text-sm text-slate-500">
                  A sandbox hub for testing logic, state management, and
                  animations.
                </p>
              </div>
            </div>

            {/* Burger Menu Button & Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 transition shadow-sm"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Dropdown Menu Container */}
              {isMenuOpen && (
                <div className="absolute right-0 mt-3 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Test Modules (Games)
                    </span>
                  </div>

                  <div className="max-h-[60vh] overflow-y-auto">
                    {modules.map((mod) => (
                      <button
                        key={mod.id}
                        onClick={() => {
                          setActiveApp(mod.id);
                          setIsMenuOpen(false); // Close menu when opening a game
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition border-b border-slate-50 last:border-0 text-left"
                      >
                        <div
                          className={`p-2 rounded-lg ${mod.bg} ${mod.color}`}
                        >
                          <mod.icon size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-slate-800">
                            {mod.name}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {mod.desc}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Empty Main Workspace Placeholder */}
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-200 rounded-2xl bg-white/50 h-[50vh]">
          <Beaker className="text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-700 mb-1">
            Workspace Ready
          </h3>
          <p className="text-slate-500 text-center text-sm">
            WMS test features will appear here.
            <br />
            this is playground I use to test function and features for fun
            related to warehouse management system, it is not part of the
            project and will not be deployed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Playground;
